/**
 * Resultset action target (#897).
 *
 * Pure helpers (no Vue/Quasar dependencies) that interpret the runtime contract of a
 * ResultSetAction (i1-core, #891/#893):
 *   execTarget, execWindowTitle, execWindowHeight, execWindowWidth, execWindowSizeUnit,
 *   execWindowMode, execWindowMoveable, execWindowSetupToken
 * plus the legacy flag linkURLTarget (former checkbox "show result in new browser tab").
 *
 * The action itself is executed exactly once by Query.vue; these helpers only decide
 * WHERE the finished result is presented.
 */

export const EXEC_TARGET = Object.freeze({
  UNDEFINED: 0,
  SAME_WINDOW: 1,
  NEW_TAB: 2,
  POPUP_DIALOG: 3
})

export const EXEC_WINDOW_SIZE_UNIT = Object.freeze({
  PIXEL: 1,
  PERCENT: 2
})

export const EXEC_WINDOW_MODE = Object.freeze({
  NOT_MODAL: 0,
  MODAL: 1
})

// Quasar dialogs keep a 24px padding around minimized dialogs; sizes are limited so the
// dialog (and its close button) always stays inside the viewport.
export const POPUP_VIEWPORT_MARGIN_PX = 48

function toInt(value) {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : NaN
  }
  if (typeof value === 'string' && /^\s*-?\d+\s*$/.test(value)) {
    return parseInt(value, 10)
  }
  return NaN
}

function toBool(value) {
  return value === true || value === 1 || value === 'true' || value === '1'
}

/**
 * Effective presentation target of an action.
 * - 2 (new tab) and 3 (popup) are honoured as configured.
 * - 1 (same window) is the explicit previous behaviour.
 * - missing / 0 / unknown values keep the previous behaviour (same window); only a legacy
 *   backend that still sends linkURLTarget=true (and no valid execTarget) maps to "new tab",
 *   exactly like the legacy mapping in ResultSetAction.setLinkURLTarget().
 */
export function resolveExecTarget(action) {
  if (!action || typeof action !== 'object') {
    return EXEC_TARGET.SAME_WINDOW
  }
  const target = toInt(action.execTarget)
  if (target === EXEC_TARGET.NEW_TAB || target === EXEC_TARGET.POPUP_DIALOG || target === EXEC_TARGET.SAME_WINDOW) {
    return target
  }
  if (action.linkURLTarget === true) {
    return EXEC_TARGET.NEW_TAB
  }
  return EXEC_TARGET.SAME_WINDOW
}

function normalizeDimension(value, unit) {
  const number = toInt(value)
  if (!Number.isFinite(number) || number <= 0) {
    return null // 0, missing or invalid = automatic size
  }
  if (unit === EXEC_WINDOW_SIZE_UNIT.PERCENT) {
    return Math.min(number, 100)
  }
  return number
}

/**
 * Normalized popup dialog configuration of an action.
 * Size unit: 1 = px, 2 = percent; missing/unknown = percent (backend default).
 * Mode: 1 = modal, 0 = not modal; missing/unknown = modal (backend default).
 * The setup token is a free string that is only passed on, never evaluated.
 */
export function popupWindowConfig(action) {
  const source = action && typeof action === 'object' ? action : {}
  const unit = toInt(source.execWindowSizeUnit) === EXEC_WINDOW_SIZE_UNIT.PIXEL ? EXEC_WINDOW_SIZE_UNIT.PIXEL : EXEC_WINDOW_SIZE_UNIT.PERCENT
  const configuredTitle = typeof source.execWindowTitle === 'string' ? source.execWindowTitle.trim() : ''
  const mode = toInt(source.execWindowMode)

  return {
    title: configuredTitle || (typeof source.title === 'string' ? source.title : ''),
    width: normalizeDimension(source.execWindowWidth, unit),
    height: normalizeDimension(source.execWindowHeight, unit),
    unit,
    modal: mode !== EXEC_WINDOW_MODE.NOT_MODAL,
    moveable: toBool(source.execWindowMoveable),
    setupToken: typeof source.execWindowSetupToken === 'string' ? source.execWindowSetupToken : null
  }
}

/**
 * Inline style for the popup card. Every dimension is limited to the viewport so the header
 * with the close button stays reachable; automatic dimensions only get the viewport limit.
 */
export function popupSizeStyle(config) {
  const cfg = config || {}
  const maxWidth = `calc(100vw - ${POPUP_VIEWPORT_MARGIN_PX}px)`
  const maxHeight = `calc(100vh - ${POPUP_VIEWPORT_MARGIN_PX}px)`
  const style = {maxWidth, maxHeight}
  const percent = cfg.unit !== EXEC_WINDOW_SIZE_UNIT.PIXEL

  if (cfg.width) {
    style.width = percent ? `min(${cfg.width}vw, ${maxWidth})` : `min(${cfg.width}px, ${maxWidth})`
  }
  if (cfg.height) {
    style.height = percent ? `min(${cfg.height}vh, ${maxHeight})` : `min(${cfg.height}px, ${maxHeight})`
  }
  return style
}

/**
 * Keep the whole dialog inside for resize/recovery calls. During dragging, an actual
 * header height permits partial offscreen placement while retaining a visible header strip.
 * `base` is the dialog rectangle without offset.
 */
export function clampDialogOffset(offset, base, viewport, headerHeight = 0) {
  const partial = Number.isFinite(headerHeight) && headerHeight > 0
  const clampAxis = (value, start, size, available, visible = null) => {
    const min = (visible === null ? 0 : visible - size) - start
    const max = (visible === null ? Math.max(0, available - size) : available - visible) - start
    if (!Number.isFinite(value)) return 0
    return Math.min(Math.max(value, Math.min(min, max)), Math.max(min, max))
  }
  return {
    x: clampAxis(offset.x, base.left, base.width, viewport.width, partial ? Math.min(64, base.width, viewport.width) : null),
    y: clampAxis(offset.y, base.top, partial ? headerHeight : base.height, viewport.height, partial ? Math.min(24, headerHeight, viewport.height) : null)
  }
}

// User-resized dimensions are pixels, irrespective of the configured initial size unit.
export function clampDialogSize(size, viewport) {
  const clamp = (value, minimum, available) => {
    const maximum = Math.max(1, available - POPUP_VIEWPORT_MARGIN_PX)
    return Math.min(maximum, Math.max(Math.min(minimum, maximum), value))
  }
  return {
    width: clamp(size.width, 320, viewport.width),
    height: clamp(size.height, 180, viewport.height)
  }
}

/**
 * Setup token of the popup dialog of an action: further dialog settings as
 * "key=value;key=value". Keys and values are case-insensitive, whitespace and line breaks
 * are ignored, unknown keys are kept but not evaluated. Known settings:
 *   appmode_splitter=open (default) | close - initial splitter state of an AppMode query
 */
export function parseSetupToken(setupToken) {
  const result = {}
  if (typeof setupToken !== 'string') return result
  for (const entry of setupToken.replace(/[\r\n]/g, '').split(';')) {
    const pos = entry.indexOf('=')
    if (pos <= 0) continue
    const key = entry.slice(0, pos).trim().toLowerCase()
    if (key) result[key] = entry.slice(pos + 1).trim()
  }
  return result
}

/** appmode_splitter=close: the AppMode query in the dialog starts with a collapsed splitter. */
export function appModeSplitterClosed(setupToken) {
  return String(parseSetupToken(setupToken).appmode_splitter || '').toLowerCase() === 'close'
}
